import { prepareHeaders } from '@/lib/cookie'
import { BASE_URL } from '@/constant'
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
export interface Project {
  // Despite the name, this is the ClickUp task id (project_task_id) that
  // clickup_people_projects keys on — not projects.project_id.
  project_id: string
  project_name: string
}

export interface Client {
  // ClickUp task id (clickup_people.id) since the Attio->ClickUp migration.
  client_id: string
  client_name: string
  link?: string
  projects: Project[]
}

export interface ClientSelectOption {
  client_id: string
  name: string
}

export interface ClientSelectOptionsResponse {
  success: boolean
  message: string
  pagination: {
    totalRecords: number
    currentPage: number
    limit: number
  }
  data: ClientSelectOption[]
}

export interface PaginatedClientsResponse {
  success: boolean
  data: Client[]
  page: number
  limit: number
  total: number
}
export interface GetClientsParams {
  page?: number
  limit?: number
  search?: string
  sort_by?: string | undefined
  order?: 'ASC' | 'DESC' | undefined
}
export interface DeleteClientPayload {
  id: string
}

export interface CreateClientPayload {
  client_name: string
  project_ids: string[]
}

export interface AttioClient {
  client_id: number
  client_name: string | null
  projects: Project[]
}

export interface AttioClientsResponse {
  success: boolean
  data: AttioClient[]
  page: number
  limit: number
  total: number
}

export interface SyncAttioClientPayload {
  id: number
  project_ids: string[]
}

export interface UpdateClientPayload extends CreateClientPayload {
  id: string
}

export const clientsApi = createApi({
  reducerPath: 'clientsApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${BASE_URL}/Clients`,
    prepareHeaders: prepareHeaders,
  }),
  tagTypes: ['Clients', 'AttioClients'],
  endpoints: builder => ({
    getClients: builder.query<PaginatedClientsResponse, GetClientsParams>({
      query: ({ page = 1, limit = 20, search = '', sort_by, order }) => ({
        url: '/',
        params: { page, limit, search, sort_by, order },
      }),
      providesTags: ['Clients'],
    }),

    // createClient: builder.mutation<
    //   { success: boolean; data: Client },
    //   CreateClientPayload
    // >({
    //   query: payload => ({
    //     url: '/',
    //     method: 'POST',
    //     body: payload,
    //   }),
    //   invalidatesTags: ['Clients'],
    // }),
    deleteClient: builder.mutation<
      { success: boolean; message: string },
      DeleteClientPayload
    >({
      query: ({ id }) => ({
        url: `/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Clients'],
    }),

    // UPDATE client
    updateClient: builder.mutation<
      { success: boolean; data: Client },
      UpdateClientPayload
    >({
      query: ({ id, ...payload }) => ({
        url: `/${id}`,
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: ['Clients'],
    }),

    getClientSelectOptions: builder.query<
      ClientSelectOptionsResponse,
      GetClientsParams
    >({
      query: ({ search = '', page, limit }) => ({
        url: '/select-option',
        method: 'GET',
        params: { search, page, limit },
      }),
      providesTags: ['Clients'],
    }),

    // Attio CRM Sync
    // getAttioClients: builder.query<AttioClientsResponse, void>({
    //   query: () => ({
    //     url: '/attio',
    //     method: 'GET',
    //     params: { limit: 1000 },
    //   }),
    //   providesTags: ['AttioClients'],
    // }),

    syncAttioClient: builder.mutation<
      { success: boolean; message: string },
      SyncAttioClientPayload
    >({
      query: ({ id, project_ids }) => ({
        url: `/attio/${id}`,
        method: 'PUT',
        body: { project_ids },
      }),
      invalidatesTags: ['Clients', 'AttioClients'],
    }),
  }),
})

export const {
  useGetClientsQuery,
  useLazyGetClientsQuery,
  // useCreateClientMutation,
  useDeleteClientMutation,
  useUpdateClientMutation,
  useGetClientSelectOptionsQuery,
  // useGetAttioClientsQuery,
  useSyncAttioClientMutation,
} = clientsApi
